/**
 * Version de esquema del almacen local y migraciones estrictamente hacia
 * adelante (FR-SYNC-6, spec/03-data-model.md §8, spec/07-deployment.md §7).
 *
 * Una version de esquema superior a la conocida detiene el arranque de forma
 * controlada: no se ejecuta ninguna migracion "hacia atras" ni se adivina un
 * formato. Degradar el esquema perderia datos en silencio.
 */

export const CURRENT_SCHEMA_VERSION = 1;

export interface Migration {
  readonly fromVersion: number;
  readonly toVersion: number;
  migrate(data: unknown): unknown;
}

export class UnknownFutureSchemaVersionError extends Error {
  constructor(storedVersion: number, knownVersion: number) {
    super(
      `El almacen local declara version de esquema ${storedVersion}, superior a la version conocida ` +
        `${knownVersion}. Arranque detenido: no se migra hacia atras ni se asume compatibilidad.`,
    );
    this.name = "UnknownFutureSchemaVersionError";
  }
}

export class NoMigrationPathError extends Error {
  constructor(fromVersion: number, toVersion: number) {
    super(`No existe ruta de migracion registrada de la version ${fromVersion} a ${toVersion}`);
    this.name = "NoMigrationPathError";
  }
}

/**
 * Calcula la secuencia ordenada de migraciones necesarias para llevar
 * `storedVersion` hasta CURRENT_SCHEMA_VERSION. Lanza si `storedVersion` es
 * superior a la version conocida, o si falta un eslabon en la cadena.
 */
export function planMigrations(
  storedVersion: number,
  registeredMigrations: readonly Migration[],
  targetVersion: number = CURRENT_SCHEMA_VERSION,
): Migration[] {
  if (storedVersion > targetVersion) {
    throw new UnknownFutureSchemaVersionError(storedVersion, targetVersion);
  }
  const byFromVersion = new Map(registeredMigrations.map((m) => [m.fromVersion, m]));
  const plan: Migration[] = [];
  let current = storedVersion;
  while (current < targetVersion) {
    const step = byFromVersion.get(current);
    if (!step) {
      throw new NoMigrationPathError(current, targetVersion);
    }
    plan.push(step);
    current = step.toVersion;
  }
  return plan;
}

/** Aplica en orden las migraciones planificadas sobre un valor de datos crudo. */
export function applyMigrations(data: unknown, plan: readonly Migration[]): unknown {
  return plan.reduce((acc, migration) => migration.migrate(acc), data);
}
